/**
 * Entity terminology utilities for handling custom client/customer/entity naming
 */

export interface EntityTermOption {
  id: string;
  singular: string;
  plural: string;
  description: string;
  icon?: string;
}

// Predefined common entity term options
export const ENTITY_TERM_OPTIONS: EntityTermOption[] = [
  {
    id: 'clients',
    singular: 'client',
    plural: 'clients',
    description: 'For agencies, consultancies, and service providers',
  },
  {
    id: 'customers',
    singular: 'customer',
    plural: 'customers',
    description: 'For retail, e-commerce, and B2C businesses',
  },
  {
    id: 'accounts',
    singular: 'account',
    plural: 'accounts',
    description: 'For B2B, SaaS, and enterprise companies',
  },
  {
    id: 'members',
    singular: 'member',
    plural: 'members',
    description: 'For associations, clubs, and membership organizations',
  },
  {
    id: 'partners',
    singular: 'partner',
    plural: 'partners',
    description: 'For partnerships and collaborative businesses',
  },
  {
    id: 'vendors',
    singular: 'vendor',
    plural: 'vendors',
    description: 'For procurement and supply chain management',
  },
  {
    id: 'patients',
    singular: 'patient',
    plural: 'patients',
    description: 'For healthcare and medical practices',
  },
  {
    id: 'students',
    singular: 'student',
    plural: 'students',
    description: 'For educational institutions',
  },
  {
    id: 'tenants',
    singular: 'tenant',
    plural: 'tenants',
    description: 'For property management and real estate',
  },
  {
    id: 'entities',
    singular: 'entity',
    plural: 'entities',
    description: 'Generic term for any type of business relationship',
  },
];

/**
 * Pluralization rules for English
 */
const PLURALIZATION_RULES: Array<{
  pattern: RegExp;
  replacement: string;
  singular?: RegExp;
}> = [
  // Irregular plurals
  { pattern: /person$/i, replacement: 'people', singular: /people$/i },
  { pattern: /child$/i, replacement: 'children', singular: /children$/i },
  { pattern: /man$/i, replacement: 'men', singular: /men$/i },
  { pattern: /woman$/i, replacement: 'women', singular: /women$/i },
  
  // Words ending in s, x, z, ch, sh
  { pattern: /(s|x|z|ch|sh)$/i, replacement: '$1es' },
  
  // Words ending in consonant + y
  { pattern: /([^aeiou])y$/i, replacement: '$1ies' },
  
  // Words ending in f or fe
  { pattern: /f$/i, replacement: 'ves' },
  { pattern: /fe$/i, replacement: 'ves' },
  
  // Words ending in consonant + o
  { pattern: /([^aeiou])o$/i, replacement: '$1oes' },
  
  // Default: just add s
  { pattern: /$/i, replacement: 's' },
];

/**
 * Get the plural form of a word
 */
export function pluralize(word: string): string {
  if (!word) return '';
  
  // Check if it's already plural (rough check)
  if (word.endsWith('s') && !word.endsWith('ss')) {
    return word;
  }
  
  // Apply pluralization rules
  for (const rule of PLURALIZATION_RULES) {
    if (rule.pattern.test(word)) {
      return word.replace(rule.pattern, rule.replacement);
    }
  }
  
  return word + 's';
}

/**
 * Get the singular form of a word (basic implementation)
 */
export function singularize(word: string): string {
  if (!word) return '';
  
  // Handle irregular plurals
  const irregulars: Record<string, string> = {
    'people': 'person',
    'children': 'child',
    'men': 'man',
    'women': 'woman',
    'feet': 'foot',
    'teeth': 'tooth',
    'geese': 'goose',
    'mice': 'mouse',
  };
  
  const irregular = irregulars[word.toLowerCase()];
  if (irregular) {
    return irregular;
  }
  
  // Handle common patterns
  if (word.endsWith('ies') && word.length > 3) {
    return word.slice(0, -3) + 'y';
  }
  
  if (word.endsWith('ves')) {
    if (word.endsWith('ives')) {
      return word.slice(0, -4) + 'ife';
    }
    return word.slice(0, -3) + 'f';
  }
  
  if (word.endsWith('es')) {
    // Check if it's from s, x, z, ch, sh
    const base = word.slice(0, -2);
    if (base.endsWith('s') || base.endsWith('x') || base.endsWith('z') || 
        base.endsWith('ch') || base.endsWith('sh')) {
      return base;
    }
    // Check if it's from consonant + o
    if (base.match(/[^aeiou]o$/)) {
      return base;
    }
  }
  
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 1) {
    return word.slice(0, -1);
  }
  
  return word;
}

/**
 * Validate if a singular/plural pair makes sense
 */
export function validateTerminology(singular: string, plural: string): {
  isValid: boolean;
  suggestedPlural?: string;
  suggestedSingular?: string;
} {
  if (!singular || !plural) {
    return { isValid: false };
  }
  
  const expectedPlural = pluralize(singular);
  const expectedSingular = singularize(plural);
  
  // If they match exactly, it's valid
  if (singular.toLowerCase() === expectedSingular.toLowerCase() && 
      plural.toLowerCase() === expectedPlural.toLowerCase()) {
    return { isValid: true };
  }
  
  // If the user's input is reasonable, accept it
  if (plural.length > singular.length || plural !== singular) {
    return { isValid: true };
  }
  
  // Suggest corrections
  return {
    isValid: false,
    suggestedPlural: expectedPlural,
    suggestedSingular: expectedSingular,
  };
}

/**
 * Format entity terminology for display
 */
export function formatEntityTerm(
  term: string,
  capitalize: boolean = false,
  articlePrefix: boolean = false
): string {
  if (!term) return '';
  
  let formatted = term.toLowerCase();
  
  if (capitalize) {
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  
  if (articlePrefix) {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const article = vowels.includes(formatted.charAt(0).toLowerCase()) ? 'an' : 'a';
    formatted = `${article} ${formatted}`;
  }
  
  return formatted;
}

/**
 * Get entity terminology from settings or default
 */
export function getEntityTerminology(settings?: { singular: string; plural: string }): {
  singular: string;
  plural: string;
} {
  return {
    singular: settings?.singular || 'entity',
    plural: settings?.plural || 'entities',
  };
}
